/**
 * PvzRaschet — Notification Service
 * Manages browser notifications, Service Worker registration, permission requests,
 * and chat mute settings.
 */

const NOTIFICATION_PERMISSION_KEY = 'pvz_notification_asked'
const CHAT_MUTES_KEY = 'pvz_chat_mutes'
const SW_MIGRATION_KEY = 'pvz_sw_migrated_v1'
const VAPID_CACHE_KEY = 'pvz_vapid_key'
export const PUSH_REPAIR_MARKER_V1A = 'pvz_push_subscription_repair_v1a'

// ========== Helpers ==========

/**
 * Преобразует URL-Safe Base64 строку в Uint8Array (для applicationServerKey)
 */
export function urlBase64ToUint8Array(base64String) {
  if (!base64String || typeof base64String !== 'string') return new Uint8Array(0)
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

/**
 * Проверяет соответствие applicationServerKey текущей подписки ожидаемому публичному ключу
 */
export function checkApplicationServerKeyMatch(subscription, expectedPublicKey) {
  try {
    if (!subscription || !subscription.options || !subscription.options.applicationServerKey) {
      const cached = localStorage.getItem(VAPID_CACHE_KEY)
      return cached === expectedPublicKey
    }
    const rawKey = subscription.options.applicationServerKey
    const keyArray = new Uint8Array(rawKey)
    const expectedArray = urlBase64ToUint8Array(expectedPublicKey)
    if (keyArray.length !== expectedArray.length) return false
    for (let i = 0; i < keyArray.length; i++) {
      if (keyArray[i] !== expectedArray[i]) return false
    }
    return true
  } catch (e) {
    const cached = localStorage.getItem(VAPID_CACHE_KEY)
    return cached === expectedPublicKey
  }
}

/**
 * Динамический запрос VAPID публичного ключа с бэкенда
 */
export async function fetchVapidPublicKey() {
  try {
    const token = localStorage.getItem('token')
    const API_URL = import.meta.env.VITE_API_URL || '/api'
    const headers = {}
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    const response = await fetch(`${API_URL}/notifications/vapid-public-key`, { headers })
    if (!response.ok) {
      return { enabled: false, publicKey: null }
    }
    const data = await response.json()
    return {
      enabled: Boolean(data?.enabled),
      publicKey: data?.publicKey || null
    }
  } catch (err) {
    console.error('[Notifications] Failed to fetch VAPID public key:', err)
    return { enabled: false, publicKey: null }
  }
}

// ========== Service Worker Lifecycle ==========

let swRegistration = null
let registrationPromise = null

/**
 * Регистрирует Service Worker с устранением состояния гонки (module-level promise)
 * и координированной одноразовой миграцией.
 */
export function registerServiceWorker() {
  if (!registrationPromise) {
    registrationPromise = performRegistrationFlow().finally(() => {
      // Сохраняем промис для предотвращения параллельных повторных регистраций
    })
  }
  return registrationPromise
}

async function performRegistrationFlow() {
  if (!('serviceWorker' in navigator)) {
    console.log('[Notifications] Service Worker not supported')
    return null
  }

  try {
    const isMigrated = localStorage.getItem(SW_MIGRATION_KEY)

    // A. ONE-TIME MIGRATION FLOW (выполняется строго один раз до установки маркера)
    if (!isMigrated) {
      console.log('[SW Migration] Running one-time legacy cleanup...')

      // 1. Дожидаемся unregister всех старых регистраций
      try {
        const regs = await navigator.serviceWorker.getRegistrations()
        for (const r of regs) {
          await r.unregister()
        }
      } catch (unregErr) {
        console.warn('[SW Migration] Warning during unregister:', unregErr)
      }

      // 2. Удаление доказанного legacy кеша (pvzraschet-v1 и pvzraschet-*)
      if ('caches' in window) {
        try {
          const cacheKeys = await caches.keys()
          for (const key of cacheKeys) {
            if (key === 'pvzraschet-v1' || key.startsWith('pvzraschet-')) {
              await caches.delete(key)
            }
          }
        } catch (cacheErr) {
          console.warn('[SW Migration] Warning during cache cleanup:', cacheErr)
        }
      }

      // 3. Регистрируем новый push-only worker
      const reg = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none'
      })

      // 4. ТОЛЬКО ПОСЛЕ УСПЕШНОЙ РЕГИСТРАЦИИ фиксируем маркер завершения
      localStorage.setItem(SW_MIGRATION_KEY, '1')
      console.log('[SW Migration] Migration successfully completed')

      swRegistration = reg
      setupSwMessageListener()
      return reg
    }

    // B. NORMAL LIFECYCLE (маркер уже установлен)
    // Штатная регистрация без unregister и без очистки CacheStorage
    const reg = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      updateViaCache: 'none'
    })
    swRegistration = reg
    setupSwMessageListener()
    return reg

  } catch (err) {
    console.error('[Notifications] SW registration error:', err)
    // ВНИМАНИЕ: маркер НЕ ставится при ошибке, следующая загрузка повторит попытку
    return null
  }
}

// ========== Notification Deep-Link Target Parser ==========

export const ROUTE_PAGE_MAP = {
  '/': 'dashboard',
  '/dashboard': 'dashboard',
  '/chat': 'chat',
  '/tasks': 'tasks',
  '/schedule': 'schedule',
  '/operations': 'operations',
  '/payments': 'payments',
  '/settings': 'settings',
  '/vacancies': 'vacancies',
  '/daily-report': 'daily_report'
}

export const NAV_TYPE_PAGE_MAP = {
  chat: 'chat',
  tasks: 'tasks',
  schedule: 'schedule',
  operations: 'operations',
  payments: 'payments',
  settings: 'settings',
  vacancies: 'vacancies',
  dashboard: 'dashboard',
  daily_report: 'daily_report',
  'daily-report': 'daily_report'
}

/**
 * Нормализует URL для безопасной relative навигации.
 * Разрешает только поддерживаемые внутренние пути приложения.
 * Fallback: '/'
 */
export function normalizeNotificationTargetUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return '/'
  const trimmed = rawUrl.trim()
  if (!trimmed) return '/'
  if (/^(javascript|data|vbscript):/i.test(trimmed)) return '/'
  if (trimmed.startsWith('//')) return '/'

  let pathWithQuery = trimmed
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    try {
      const parsed = new URL(trimmed)
      if (typeof window !== 'undefined' && window.location && parsed.origin !== window.location.origin) {
        return '/'
      }
      if (typeof location !== 'undefined' && parsed.origin !== location.origin) {
        return '/'
      }
      pathWithQuery = parsed.pathname + parsed.search
    } catch (e) {
      return '/'
    }
  }

  if (!pathWithQuery.startsWith('/')) {
    pathWithQuery = '/' + pathWithQuery
  }

  const questionIdx = pathWithQuery.indexOf('?')
  const pathname = questionIdx === -1 ? pathWithQuery : pathWithQuery.substring(0, questionIdx)
  const search = questionIdx === -1 ? '' : pathWithQuery.substring(questionIdx)

  if (!Object.prototype.hasOwnProperty.call(ROUTE_PAGE_MAP, pathname)) {
    return '/'
  }

  return pathname + search
}

/**
 * Извлекает и валидирует цель навигации из payload уведомления.
 * Приоритеты:
 * 1. Валидный data.url (primary target)
 * 2. navType (обратная совместимость)
 * 3. type (второй fallback)
 * 4. 'dashboard' (безопасный fallback)
 */
export function parseNotificationTarget(data) {
  if (!data || typeof data !== 'object') {
    return {
      page: 'dashboard',
      url: '/',
      pathname: '/',
      queryParams: {},
      targetType: null,
      targetId: null,
      targetTab: null
    }
  }

  let rawUrl = data.url
  if (!rawUrl && data.data && typeof data.data === 'object' && data.data.url) {
    rawUrl = data.data.url
  }

  const navType = data.navType || null
  const type = data.type || null
  const targetType = data.targetType || null
  const targetId = data.targetId || null
  const targetTab = data.targetTab || null

  // 1. Primary target: valid data.url
  if (rawUrl && typeof rawUrl === 'string') {
    const normalized = normalizeNotificationTargetUrl(rawUrl)
    const isExplicitRoot = (rawUrl.trim() === '/' || rawUrl.trim() === '/dashboard')
    if (normalized !== '/' || isExplicitRoot) {
      const questionIdx = normalized.indexOf('?')
      const pathname = questionIdx === -1 ? normalized : normalized.substring(0, questionIdx)
      const search = questionIdx === -1 ? '' : normalized.substring(questionIdx)

      const queryParams = {}
      if (search) {
        try {
          const sp = new URLSearchParams(search)
          for (const [k, v] of sp.entries()) {
            queryParams[k] = v
          }
        } catch (e) {}
      }

      const page = ROUTE_PAGE_MAP[pathname] || 'dashboard'
      const effectiveTab = targetTab || queryParams.tab || null

      return {
        page,
        url: normalized,
        pathname,
        queryParams,
        targetType,
        targetId,
        targetTab: effectiveTab
      }
    }
  }

  // 2. Backward-compatible fallback: navType
  if (navType && NAV_TYPE_PAGE_MAP[navType]) {
    const page = NAV_TYPE_PAGE_MAP[navType]
    const defaultPath = page === 'dashboard' ? '/' : `/${page}`
    return {
      page,
      url: defaultPath,
      pathname: defaultPath,
      queryParams: targetTab ? { tab: targetTab } : {},
      targetType,
      targetId,
      targetTab
    }
  }

  // 3. Second fallback: type
  if (type && NAV_TYPE_PAGE_MAP[type]) {
    const page = NAV_TYPE_PAGE_MAP[type]
    const defaultPath = page === 'dashboard' ? '/' : `/${page}`
    return {
      page,
      url: defaultPath,
      pathname: defaultPath,
      queryParams: targetTab ? { tab: targetTab } : {},
      targetType,
      targetId,
      targetTab
    }
  }

  // 4. Safe default: dashboard
  return {
    page: 'dashboard',
    url: '/',
    pathname: '/',
    queryParams: {},
    targetType,
    targetId,
    targetTab
  }
}

let swMessageListenerAttached = false

function setupSwMessageListener() {
  if (!navigator.serviceWorker || swMessageListenerAttached) return
  swMessageListenerAttached = true

  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'NOTIFICATION_CLICK') {
      const target = parseNotificationTarget(event.data.data)

      if (target.page === 'chat') {
        try {
          localStorage.setItem('wb_chat_navigate', JSON.stringify({
            type: target.targetType || 'channel',
            id: target.targetId
          }))
        } catch (e) {}
      } else if (target.page === 'tasks') {
        if (target.targetId) {
          try { localStorage.setItem('wb_task_navigate', target.targetId) } catch (e) {}
        }
      } else if (target.page === 'operations') {
        const tab = target.targetTab || target.queryParams?.tab
        if (tab) {
          try { localStorage.setItem('default_operations_tab', tab) } catch (e) {}
          window.dispatchEvent(new CustomEvent('operations-tab-navigate', { detail: { tab } }))
        }
      } else if (target.page === 'settings') {
        const tab = target.targetTab || target.queryParams?.tab
        if (tab) {
          try { localStorage.setItem('wb_settings_tab', tab) } catch (e) {}
          window.dispatchEvent(new CustomEvent('settings-tab-navigate', { detail: { tab } }))
        }
      } else if (target.page === 'daily_report') {
        const reportParams = {
          pvzId: target.queryParams?.pvzId || null,
          date: target.queryParams?.date || null
        }
        try { localStorage.setItem('wb_daily_report_navigate', JSON.stringify(reportParams)) } catch (e) {}
        window.dispatchEvent(new CustomEvent('daily-report-navigate', { detail: reportParams }))
      }

      // Safe SPA navigation without hard reload or location API
      window.dispatchEvent(new CustomEvent('navigateTo', { detail: target.page }))
    }
  })
}

// ========== Push Subscription Management ==========

/**
 * Подписать устройство на пуш-уведомления с динамическим ключом и защитой от конфликта (409 repair)
 */
export async function subscribeToPushNotifications(registration, options = {}) {
  if (!registration || !('pushManager' in registration)) {
    console.log('[Notifications] PushManager not supported')
    return null
  }

  const { isRetry = false } = options

  try {
    const config = await fetchVapidPublicKey()
    if (!config || !config.enabled || !config.publicKey) {
      console.log('[Notifications] Web Push is disabled or unavailable on server')
      return null
    }

    const serverPublicKey = config.publicKey
    let subscription = await registration.pushManager.getSubscription()
    let createdFreshSubscription = false

    if (subscription) {
      const isKeyMatch = checkApplicationServerKeyMatch(subscription, serverPublicKey)
      if (!isKeyMatch) {
        console.log('[Notifications] Subscription key changed, renewing...')
        const oldEndpoint = subscription.endpoint
        await subscription.unsubscribe()
        const token = localStorage.getItem('token')
        if (token && oldEndpoint) {
          const API_URL = import.meta.env.VITE_API_URL || '/api'
          await fetch(`${API_URL}/notifications/unsubscribe`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ endpoint: oldEndpoint })
          }).catch(() => {})
        }
        subscription = null
      }
    }

    if (!subscription) {
      const convertedVapidKey = urlBase64ToUint8Array(serverPublicKey)
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedVapidKey
      })
      createdFreshSubscription = true
      console.log('[Notifications] Push subscription created in browser')
    }

    const token = localStorage.getItem('token')
    if (token && subscription) {
      const API_URL = import.meta.env.VITE_API_URL || '/api'
      const response = await fetch(`${API_URL}/notifications/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(subscription)
      })

      // Обработка 409 ENDPOINT_CONFLICT: чужой профиль в БД владел этим endpoint
      if (response.status === 409 && !isRetry) {
        console.warn('[Notifications] 409 ENDPOINT_CONFLICT received. Performing 1-time controlled repair...')
        await subscription.unsubscribe()
        const convertedVapidKey = urlBase64ToUint8Array(serverPublicKey)
        const freshSub = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: convertedVapidKey
        })
        const retryRes = await fetch(`${API_URL}/notifications/subscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(freshSub)
        })

        if (retryRes.ok) {
          localStorage.setItem(VAPID_CACHE_KEY, serverPublicKey)
          localStorage.setItem(PUSH_REPAIR_MARKER_V1A, 'done')
          console.log('[Notifications] Repaired subscription saved on backend')
          return freshSub
        } else {
          console.error('[Notifications] 409 repair retry failed with status:', retryRes.status)
          return null
        }
      }

      if (response.ok) {
        localStorage.setItem(VAPID_CACHE_KEY, serverPublicKey)
        if (createdFreshSubscription) {
          localStorage.setItem(PUSH_REPAIR_MARKER_V1A, 'done')
        }
        console.log('[Notifications] Push subscription saved on backend')
      } else {
        console.error('[Notifications] Failed to save subscription on backend, status:', response.status)
      }
    }

    return subscription
  } catch (err) {
    console.error('[Notifications] Push subscription failed:', err)
    return null
  }
}

/**
 * Отписать устройство от пуш-уведомлений
 */
export async function unsubscribeFromPushNotifications() {
  if (!swRegistration) return

  try {
    const subscription = await swRegistration.pushManager.getSubscription()
    if (subscription) {
      const endpoint = subscription.endpoint
      await subscription.unsubscribe()

      const token = localStorage.getItem('token')
      if (token && endpoint) {
        const API_URL = import.meta.env.VITE_API_URL || '/api'
        await fetch(`${API_URL}/notifications/unsubscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ endpoint })
        })
      }
      localStorage.removeItem(VAPID_CACHE_KEY)
      console.log('[Notifications] Push subscription removed')
    }
  } catch (err) {
    console.error('[Notifications] Push unsubscription failed:', err)
  }
}

// ========== Permission Management ==========

/**
 * Запрос разрешений с предварительной проверкой готовности бэкенда
 */
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    console.log('[Notifications] Notifications API not supported')
    return 'denied'
  }

  // Не тратим prompt пользователя, если push выключен на сервере
  const config = await fetchVapidPublicKey()
  if (!config || !config.enabled || !config.publicKey) {
    console.log('[Notifications] Push notifications disabled or unavailable on server')
    return 'disabled'
  }

  if (Notification.permission === 'granted') {
    if (swRegistration) {
      subscribeToPushNotifications(swRegistration)
    }
    return 'granted'
  }

  if (Notification.permission === 'denied') return 'denied'

  try {
    const result = await Notification.requestPermission()
    localStorage.setItem(NOTIFICATION_PERMISSION_KEY, 'true')
    if (result === 'granted' && swRegistration) {
      subscribeToPushNotifications(swRegistration)
    }
    return result
  } catch (err) {
    console.error('[Notifications] Permission request failed:', err)
    return 'denied'
  }
}

/**
 * Проверка необходимости показа баннера разрешений
 */
export function shouldAskPermission() {
  if (!('Notification' in window)) return false
  if (Notification.permission !== 'default') return false
  if (localStorage.getItem(NOTIFICATION_PERMISSION_KEY)) return false
  return true
}

/**
 * Текущий статус разрешения
 */
export function getPermissionStatus() {
  if (!('Notification' in window)) return 'unsupported'
  return Notification.permission
}

// ========== V1A Push Subscription Repair ==========

/**
 * Однократная контролируемая переподписка / починка подписки V1A (Migration-style).
 * Если маркер pvz_push_subscription_repair_v1a уже установлен, выполняется стандартная синхронизация.
 */
export async function repairPushSubscriptionV1A(registration) {
  if (!registration || !('pushManager' in registration)) {
    console.log('[Notifications] PushManager not supported')
    return null
  }

  const isRepaired = localStorage.getItem(PUSH_REPAIR_MARKER_V1A) === 'done'
  if (isRepaired) {
    return subscribeToPushNotifications(registration)
  }

  console.log('[Notifications] Starting one-time V1A push subscription repair...')

  try {
    const config = await fetchVapidPublicKey()
    if (!config || !config.enabled || !config.publicKey) {
      console.log('[Notifications] Web Push is disabled or unavailable on server')
      return null
    }

    const serverPublicKey = config.publicKey

    // 1. Получить существующую подписку браузера
    const existingSub = await registration.pushManager.getSubscription()

    // 2. Если есть: подтверждённая отписка на бэкенде перед browser unsubscribe
    if (existingSub) {
      const oldEndpoint = existingSub.endpoint
      const token = localStorage.getItem('token')

      if (token && oldEndpoint) {
        const API_URL = import.meta.env.VITE_API_URL || '/api'
        let unsubRes = null
        try {
          unsubRes = await fetch(`${API_URL}/notifications/unsubscribe`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ endpoint: oldEndpoint })
          })
        } catch (fetchErr) {
          console.error('[Notifications] Backend unsubscribe network error during V1A repair:', fetchErr)
          return null
        }

        // 3. Проверить HTTP response. Если НЕ ok — STOP rotation, не трогать browser sub
        if (!unsubRes || !unsubRes.ok) {
          console.error('[Notifications] Backend unsubscribe failed during V1A repair, status:', unsubRes?.status)
          return null
        }

        // 4. Backend unsubscribe ok (removed: 0 или 1)
        try {
          const unsubData = await unsubRes.json().catch(() => null)
          console.log('[Notifications] Backend unsubscribe confirmed during V1A repair, removed:', unsubData?.removed)
        } catch (e) {}
      }

      // 5. После подтверждённого backend cleanup вызываем existingSub.unsubscribe()
      try {
        const unsubSuccess = await existingSub.unsubscribe()
        if (!unsubSuccess) {
          console.error('[Notifications] Browser unsubscribe returned false during V1A repair')
          return null
        }
        console.log('[Notifications] Unsubscribed stale browser subscription during V1A repair')
      } catch (unsubErr) {
        console.error('[Notifications] Browser unsubscribe threw error during V1A repair:', unsubErr)
        return null
      }
    }

    // 6. Только после успешного browser unsubscribe: создаём fresh subscription
    const convertedVapidKey = urlBase64ToUint8Array(serverPublicKey)
    const freshSub = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: convertedVapidKey
    })

    // 7. Отправить новую подписку на бэкенд
    const token = localStorage.getItem('token')
    if (token && freshSub) {
      const API_URL = import.meta.env.VITE_API_URL || '/api'
      const response = await fetch(`${API_URL}/notifications/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(freshSub)
      })

      // 8. Только если backend save response ok: фиксируем маркер 'done'
      if (response.ok) {
        localStorage.setItem(VAPID_CACHE_KEY, serverPublicKey)
        localStorage.setItem(PUSH_REPAIR_MARKER_V1A, 'done')
        console.log('[Notifications] V1A push subscription repair completed successfully')
        return freshSub
      } else {
        console.error('[Notifications] V1A push subscription repair backend save failed, status:', response.status)
        return null
      }
    }

    return freshSub
  } catch (err) {
    console.error('[Notifications] V1A push subscription repair failed:', err)
    return null
  }
}

export const syncOrRepairPushSubscription = repairPushSubscriptionV1A

// ========== Browser Notifications ==========

/**
 * Send a browser notification
 * Uses Service Worker if available (works better on mobile),
 * falls back to new Notification() on desktop.
 *
 * Shows notification ONLY when tab is NOT focused (user is elsewhere).
 */
export async function showBrowserNotification(title, options = {}) {
  const debugLog = (msg, data = '') => {
    const formatted = `[Notif Debug] ${msg} ${data ? JSON.stringify(data) : ''}`
    console.log(formatted)
    try {
      const logs = JSON.parse(localStorage.getItem('pvz_notif_logs') || '[]')
      logs.push({ time: new Date().toLocaleTimeString(), msg, data })
      if (logs.length > 20) logs.shift()
      localStorage.setItem('pvz_notif_logs', JSON.stringify(logs))
    } catch (e) {}
  }

  debugLog('showBrowserNotification called', { title, permission: typeof Notification !== 'undefined' ? Notification.permission : 'N/A', visibilityState: document.visibilityState, hidden: document.hidden })

  if (!('Notification' in window)) {
    debugLog('Notification API not supported in window')
    return null
  }
  if (Notification.permission !== 'granted') {
    debugLog('Permission is not granted', Notification.permission)
    return null
  }

  // Если вкладка открыта и активна — не показываем системное уведомление ОС,
  // так как пользователь уже видит Toast-уведомление в самом интерфейсе.
  if (document.visibilityState === 'visible' && !document.hidden) {
    debugLog('Skipping OS notification because tab is active/visible')
    return null
  }

  // Если вкладка свернута (в фоне) и нет флага forceLocal — пропускаем локальное
  // уведомление, так как это событие продублируется через Web Push (Service Worker).
  if (document.visibilityState === 'hidden' && !options.forceLocal) {
    debugLog('Skipping background local notification because Web Push will deliver it')
    return null
  }

  const defaults = {
    icon: '/icons/icon-192.png',
    badge: '/icons/icon-192.png',
    vibrate: [100, 50, 100],
    tag: 'pvzraschet-' + (options.tag || 'msg'),
    renotify: true,
    silent: false,
  }

  const notifOptions = { ...defaults, ...options }

  // Prefer Service Worker notification (works in background on mobile)
  if (swRegistration) {
    try {
      debugLog('Attempting Service Worker showNotification', { title, tag: notifOptions.tag })
      await swRegistration.showNotification(title, {
        ...notifOptions,
        data: {
          url: options.url || '/',
          type: options.navType || 'general',
          targetType: options.targetType,
          targetId: options.targetId,
        }
      })
      debugLog('Service Worker showNotification call completed successfully')
      return true
    } catch (err) {
      debugLog('SW showNotification failed, trying fallback', err.message || err)
      console.warn('[Notifications] SW showNotification failed, falling back:', err)
    }
  }

  // Fallback to regular Notification
  try {
    debugLog('Attempting fallback new Notification()', { title, tag: notifOptions.tag })
    const notification = new Notification(title, notifOptions)

    notification.onclick = () => {
      debugLog('Fallback notification clicked')
      window.focus()
      notification.close()
      if (options.onClick) options.onClick()
    }

    notification.onerror = (e) => {
      debugLog('Fallback notification error event triggered', e)
    }

    // Auto-close after 8 seconds
    setTimeout(() => {
      try {
        notification.close()
        debugLog('Fallback notification auto-closed')
      } catch (e) {}
    }, 8000)

    debugLog('Fallback notification created successfully')
    return notification
  } catch (err) {
    debugLog('Fallback new Notification() threw error', err.message || err)
    console.error('[Notifications] Failed to create notification:', err)
    return null
  }
}

// ========== Chat Mute Management ==========

/**
 * Get all mute settings from localStorage
 * Format: { [chatId]: { mutedUntil: timestamp|null, mutedAt: timestamp } }
 * mutedUntil = null means muted forever
 */
function getMuteSettings() {
  try {
    const raw = localStorage.getItem(CHAT_MUTES_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

function saveMuteSettings(settings) {
  localStorage.setItem(CHAT_MUTES_KEY, JSON.stringify(settings))
}

/**
 * Mute a chat for a duration
 * @param {string} chatId - topic or room ID
 * @param {number|null} durationMinutes - null = forever, number = minutes
 */
export function muteChat(chatId, durationMinutes = null) {
  const settings = getMuteSettings()
  const now = Date.now()

  settings[chatId] = {
    mutedAt: now,
    mutedUntil: durationMinutes ? now + (durationMinutes * 60 * 1000) : null,
    durationLabel: durationMinutes
      ? (durationMinutes >= 60 ? `${durationMinutes / 60}ч` : `${durationMinutes}м`)
      : 'навсегда'
  }

  saveMuteSettings(settings)
  return settings[chatId]
}

/**
 * Unmute a chat
 */
export function unmuteChat(chatId) {
  const settings = getMuteSettings()
  delete settings[chatId]
  saveMuteSettings(settings)
}

/**
 * Check if a chat is currently muted
 */
export function isChatMuted(chatId) {
  const settings = getMuteSettings()
  const mute = settings[chatId]
  if (!mute) return false

  // Muted forever (mutedUntil === null)
  if (mute.mutedUntil === null) return true

  // Check if mute expired
  if (Date.now() >= mute.mutedUntil) {
    // Auto-cleanup expired mute
    delete settings[chatId]
    saveMuteSettings(settings)
    return false
  }

  return true
}

/**
 * Get mute info for a chat (for display)
 * Returns null if not muted, or { mutedUntil, durationLabel, remaining }
 */
export function getMuteInfo(chatId) {
  const settings = getMuteSettings()
  const mute = settings[chatId]
  if (!mute) return null

  // Check expiry
  if (mute.mutedUntil !== null && Date.now() >= mute.mutedUntil) {
    delete settings[chatId]
    saveMuteSettings(settings)
    return null
  }

  const remaining = mute.mutedUntil
    ? Math.max(0, Math.ceil((mute.mutedUntil - Date.now()) / 60000))
    : null

  return {
    ...mute,
    remaining, // minutes remaining, null = forever
    remainingLabel: remaining === null
      ? 'Навсегда'
      : remaining >= 60
        ? `${Math.floor(remaining / 60)}ч ${remaining % 60}м`
        : `${remaining}м`
  }
}

/**
 * Get all muted chat IDs (for owner view)
 */
export function getAllMutedChats() {
  const settings = getMuteSettings()
  const result = []
  const now = Date.now()

  for (const [chatId, mute] of Object.entries(settings)) {
    if (mute.mutedUntil === null || now < mute.mutedUntil) {
      result.push({ chatId, ...mute })
    }
  }

  return result
}
